import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Building2,
    User,
    Mail,
    Phone,
    Globe,
    DollarSign,
    Calendar,
    Tag,
    FileText,
    CheckCircle,
    AlertCircle,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Plus,
    Minus,
    Briefcase,
} from 'lucide-react';
import { partnershipsService } from '../../services/partnerships.service';

interface SocialMedia {
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    tiktok?: string;
}

interface Deliverable {
    contentType: string;
    title: string;
    description?: string;
    deadline?: string;
}

interface PublicFormData {
    brand: string;
    contact: string;
    email: string;
    value: number;
    deliveryDate?: string;
    notes?: string;
    tags?: string[];
    phone?: string;
    website?: string;
    socialMedia?: SocialMedia;
    campaignType?: string;
    campaignDescription?: string;
    deliverables?: Deliverable[];
}

export default function PublicPartnershipFormPage() {
    const { teamId } = useParams<{ teamId: string }>();

    const [isLoading, setIsLoading] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const [formData, setFormData] = useState<PublicFormData>({
        brand: '',
        contact: '',
        email: '',
        value: 0,
        deliveryDate: '',
        notes: '',
        tags: [],
        phone: '',
        website: '',
        socialMedia: { instagram: '', twitter: '', linkedin: '', tiktok: '' },
        campaignType: '',
        campaignDescription: '',
        deliverables: [{ contentType: '', title: '', description: '', deadline: '' }],
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [tagInput, setTagInput] = useState('');

    const contentTypes = [
        'Video',
        'Photo Post',
        'Story',
        'Carousel',
        'Reel / Short Video',
        'Long Video',
        'Blog / Article',
        'UGC (Brand can reuse)'
    ];

    const campaignTypes = [
        'Product Promotion',
        'Brand Awareness',
        'Seasonal Campaign',
        'Offer / Discount Campaign',
        'New Launch Campaign',
        'Review / Testimonial',
        'Giveaway',
        'Event / Store Visit',
        'Affiliate / Performance',
        'UGC Creation Only'
    ];

    const steps = [
        { number: 1, title: 'Basic Info', icon: User },
        { number: 2, title: 'Details', icon: Briefcase },
        { number: 3, title: 'Notes', icon: FileText },
    ];

    useEffect(() => {
        const validateTeam = async () => {
            if (!teamId) {
                setError('Invalid link. Please request a new partnership form link.');
                setIsLoading(false);
                return;
            }

            try {
                const result = await partnershipsService.validatePublicTeam(teamId);
                setIsValid(result);
                if (!result) {
                    setError('This link is invalid. Please request a new link.');
                }
            } catch (err) {
                setError('Unable to validate link. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        validateTeam();
    }, [teamId]);

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 1) {
            if (!formData.brand.trim()) newErrors.brand = 'Brand name is required';
            if (!formData.contact.trim()) newErrors.contact = 'Contact name is required';
            if (!formData.email.trim()) newErrors.email = 'Email is required';
            if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Step 2 validation
        if (step === 2) {
            if (!formData.value || formData.value <= 0) newErrors.value = 'Budget value is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 3));
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        setErrors({});
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSocialMediaChange = (platform: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            socialMedia: { ...prev.socialMedia, [platform]: value }
        }));
    };

    const handleTagsChange = (value: string) => {
        setTagInput(value);
        const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
        setFormData(prev => ({ ...prev, tags }));
    };

    const handleDeliverableChange = (index: number, field: 'contentType' | 'title' | 'description' | 'deadline', value: string) => {
        const newDeliverables = [...(formData.deliverables || [{ contentType: '', title: '', description: '', deadline: '' }])];
        newDeliverables[index] = {
            ...newDeliverables[index],
            [field]: value
        };
        setFormData(prev => ({ ...prev, deliverables: newDeliverables }));
    };

    const addDeliverable = () => {
        setFormData(prev => ({
            ...prev,
            deliverables: [...(prev.deliverables || []), { contentType: '', title: '', description: '', deadline: '' }]
        }));
    };

    const removeDeliverable = (index: number) => {
        const newDeliverables = formData.deliverables?.filter((_, i) => i !== index) || [];
        setFormData(prev => ({
            ...prev,
            deliverables: newDeliverables.length > 0 ? newDeliverables : [{ contentType: '', title: '', description: '', deadline: '' }]
        }));
    };

    const handleSubmit = async () => {
        if (!validateStep(1) || !validateStep(2)) {
            setCurrentStep(1);
            return;
        }

        if (!teamId) return;

        setIsSubmitting(true);
        try {
            const submitData: Parameters<typeof partnershipsService.createFromPublicForm>[1] = {
                brand: formData.brand,
                contact: formData.contact,
                email: formData.email,
                value: formData.value,
                notes: formData.notes,
                tags: formData.tags,
                phone: formData.phone,
                website: formData.website,
                socialMedia: formData.socialMedia,
                campaignType: formData.campaignType,
                campaignDescription: formData.campaignDescription,
                deliverables: formData.deliverables,
            };
            if (formData.deliveryDate) {
                submitData.deliveryDate = new Date(formData.deliveryDate).toISOString();
            }
            await partnershipsService.createFromPublicForm(teamId, submitData);
            setIsSubmitted(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-[133.33vh] flex items-center justify-center p-4 relative">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
                    }}
                />
                <div className="absolute inset-0 bg-primary-900/40 backdrop-blur-[2px]" />
                <div className="relative z-10 text-center px-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-white/40 border-t-white rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-sm sm:text-base text-white/80">Loading partnership form...</p>
                    <div className="mt-6 sm:mt-8 flex items-center justify-center gap-2">
                        <img src="/logo.png" alt="SubRack" className="w-6 h-6 sm:w-8 sm:h-8 object-contain" />
                        <span className="font-bold text-white/60 text-sm sm:text-base">SubRack</span>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !isValid) {
        return (
            <div className="min-h-[133.33vh] flex items-center justify-center p-3 sm:p-4 relative">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
                    }}
                />
                <div className="absolute inset-0 bg-red-900/60 backdrop-blur-[2px]" />
                <div className="relative z-10 bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center border border-white/10 mx-2">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                        <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-400" />
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Link Expired or Invalid</h1>
                    <p className="text-sm sm:text-base text-white/70 mb-4 sm:mb-6">{error}</p>
                    <p className="text-xs sm:text-sm text-white/50">
                        Please contact the creator to request a new partnership form link.
                    </p>
                    <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10 flex items-center justify-center gap-2">
                        <img src="/logo.png" alt="SubRack" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                        <span className="font-bold text-white/60 text-sm sm:text-base">SubRack</span>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (isSubmitted) {
        return (
            <div className="min-h-[133.33vh] flex items-center justify-center p-3 sm:p-4 relative">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
                    }}
                />
                <div className="absolute inset-0 bg-emerald-900/60 backdrop-blur-[2px]" />
                <div className="relative z-10 bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center border border-white/10 mx-2">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg shadow-emerald-500/30">
                        <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Partnership Submitted!</h1>
                    <p className="text-sm sm:text-base text-white/70 mb-4 sm:mb-6">
                        Thank you for your interest! The creator will review your partnership proposal and get back to you soon.
                    </p>
                    <p className="text-xs sm:text-sm text-white/50">
                        You can now close this window.
                    </p>
                    <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10 flex items-center justify-center gap-2">
                        <img src="/logo.png" alt="SubRack" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                        <span className="font-bold text-white/60 text-sm sm:text-base">SubRack</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[133.33vh] py-4 sm:py-8 px-3 sm:px-4 relative">
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                    backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
                }}
            />
            <div className="absolute inset-0 bg-primary-900/40 backdrop-blur-[2px]" />
            <div className="relative z-10 max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6 sm:mb-8">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <Building2 className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Partnership Proposal</h1>
                    <p className="text-sm sm:text-base text-white/60 px-4">Tell us about your brand and partnership opportunity</p>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
                    {/* Step Indicator - Mobile: numbered circles with progress bar */}
                    <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                        {/* Mobile Step Indicator */}
                        <div className="sm:hidden">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-medium text-gray-500">Step {currentStep} of {steps.length}</span>
                                <span className="text-xs font-semibold text-primary-600">{steps[currentStep - 1].title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {steps.map((step, index) => (
                                    <div key={step.number} className="flex items-center flex-1">
                                        <button
                                            onClick={() => setCurrentStep(step.number)}
                                            className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${currentStep === step.number
                                                ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30 scale-110'
                                                : currentStep > step.number
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-200 text-gray-500'
                                                }`}
                                        >
                                            {currentStep > step.number ? '✓' : step.number}
                                        </button>
                                        {index < steps.length - 1 && (
                                            <div className={`flex-1 h-1 mx-1 rounded-full ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Desktop Step Indicator */}
                        <div className="hidden sm:flex items-center justify-between">
                            {steps.map((step, index) => (
                                <div key={step.number} className="flex items-center">
                                    <button
                                        onClick={() => setCurrentStep(step.number)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${currentStep === step.number
                                            ? 'bg-primary-100 text-primary-600 shadow-sm'
                                            : currentStep > step.number
                                                ? 'text-green-600 bg-green-50'
                                                : 'text-gray-400 hover:text-gray-600'
                                            }`}
                                    >
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === step.number
                                            ? 'bg-primary-600 text-white'
                                            : currentStep > step.number
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-300 text-white'
                                            }`}>
                                            {currentStep > step.number ? '✓' : step.number}
                                        </div>
                                        <span className="text-sm font-medium">{step.title}</span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-3 w-12 rounded-full ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-6 max-h-[60vh] sm:max-h-[50vh] overflow-y-auto">
                        {/* Step 1: Basic Info */}
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Brand Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.brand}
                                            onChange={(e) => handleInputChange('brand', e.target.value)}
                                            className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.brand ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            placeholder="e.g. TechFlow Solutions"
                                        />
                                        {errors.brand && <p className="text-red-600 text-sm mt-1">{errors.brand}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Contact Person <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.contact}
                                            onChange={(e) => handleInputChange('contact', e.target.value)}
                                            className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.contact ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            placeholder="e.g. Sarah Johnson"
                                        />
                                        {errors.contact && <p className="text-red-600 text-sm mt-1">{errors.contact}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                placeholder="sarah@techflow.com"
                                            />
                                        </div>
                                        {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="tel"
                                                value={formData.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                placeholder="+1 (555) 123-4567"
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                                        <div className="relative">
                                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="url"
                                                value={formData.website || ''}
                                                onChange={(e) => handleInputChange('website', e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                placeholder="https://example.com"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Social Media */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Profiles</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            value={formData.socialMedia?.instagram || ''}
                                            onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                                            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            placeholder="Instagram username"
                                        />
                                        <input
                                            type="text"
                                            value={formData.socialMedia?.twitter || ''}
                                            onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                                            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            placeholder="Twitter handle"
                                        />
                                        <input
                                            type="text"
                                            value={formData.socialMedia?.linkedin || ''}
                                            onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                                            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            placeholder="LinkedIn profile"
                                        />
                                        <input
                                            type="text"
                                            value={formData.socialMedia?.tiktok || ''}
                                            onChange={(e) => handleSocialMediaChange('tiktok', e.target.value)}
                                            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            placeholder="TikTok username"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Details */}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Budget / Value <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.value}
                                                onChange={(e) => handleInputChange('value', Number(e.target.value))}
                                                className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.value ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                placeholder="2500"
                                            />
                                        </div>
                                        {errors.value && <p className="text-red-600 text-sm mt-1">{errors.value}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Delivery Date</label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="date"
                                                value={formData.deliveryDate}
                                                onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                                        <div className="relative">
                                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={tagInput}
                                                onChange={(e) => handleTagsChange(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                placeholder="tech, software, B2B"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                                    <select
                                        value={formData.campaignType || ''}
                                        onChange={(e) => handleInputChange('campaignType', e.target.value)}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                    >
                                        <option value="">Select campaign type...</option>
                                        {campaignTypes.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Description</label>
                                    <textarea
                                        value={formData.campaignDescription || ''}
                                        onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
                                        rows={3}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                        placeholder="Brief description of the campaign..."
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-medium text-gray-700">Deliverables</label>
                                        <button
                                            type="button"
                                            onClick={addDeliverable}
                                            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Deliverable
                                        </button>
                                    </div>
                                    <div className="space-y-4">
                                        {formData.deliverables?.map((deliverable, index) => (
                                            <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-xs font-medium text-gray-500">
                                                        Deliverable {index + 1}
                                                    </span>
                                                    {formData.deliverables && formData.deliverables.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeDeliverable(index)}
                                                            className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        >
                                                            <Minus className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="space-y-3">
                                                    {/* Row 1: Content Type + Deadline */}
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs text-gray-500 mb-1">Content Type</label>
                                                            <select
                                                                value={deliverable.contentType}
                                                                onChange={(e) => handleDeliverableChange(index, 'contentType', e.target.value)}
                                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                            >
                                                                <option value="">Select type...</option>
                                                                {contentTypes.map(type => (
                                                                    <option key={type} value={type}>{type}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-500 mb-1">Deadline</label>
                                                            <input
                                                                type="date"
                                                                value={deliverable.deadline || ''}
                                                                onChange={(e) => handleDeliverableChange(index, 'deadline', e.target.value)}
                                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* Row 2: Title */}
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Title</label>
                                                        <input
                                                            type="text"
                                                            value={deliverable.title}
                                                            onChange={(e) => handleDeliverableChange(index, 'title', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                            placeholder="e.g., Product Review Video"
                                                        />
                                                    </div>
                                                    {/* Row 3: Description */}
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
                                                        <textarea
                                                            value={deliverable.description || ''}
                                                            onChange={(e) => handleDeliverableChange(index, 'description', e.target.value)}
                                                            rows={2}
                                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                                            placeholder="Specific requirements for this deliverable..."
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Notes */}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        rows={6}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                        placeholder="Tell us more about your brand, campaign goals, and why you'd like to partner..."
                                    />
                                </div>

                                {/* Summary */}
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <h5 className="font-medium text-gray-900 mb-3">Partnership Summary</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-600">Brand:</span>
                                            <span className="ml-2 font-medium text-gray-900">{formData.brand}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Contact:</span>
                                            <span className="ml-2 font-medium text-gray-900">{formData.contact}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Budget:</span>
                                            <span className="ml-2 font-medium text-green-600">${formData.value.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50">
                        <button
                            type="button"
                            onClick={() => currentStep > 1 ? handleBack() : null}
                            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 hover:text-gray-900 transition-colors ${currentStep === 1 ? 'invisible' : ''
                                }`}
                            disabled={isSubmitting}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Back
                        </button>

                        {currentStep < 3 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="hidden sm:inline">Submitting...</span>
                                        <span className="sm:hidden">...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="hidden sm:inline">Submit Partnership</span>
                                        <span className="sm:hidden">Submit</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-6 sm:mt-8 space-y-2 sm:space-y-3 px-4">
                    <p className="text-xs sm:text-sm text-white/60">
                        🔒 This is a secure form. Your information will be reviewed by the creator.
                    </p>
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-white/40 text-xs sm:text-sm">Powered by</span>
                        <div className="flex items-center gap-1.5">
                            <img src="/logo.png" alt="SubRack" className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />
                            <span className="font-bold text-white/60 text-sm sm:text-base">SubRack</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
